import { useCallback, useEffect, useState } from "react";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import Button from "@/components/Button/Button";
import PlusIcon from "@/components/SVGComponents/PlusIcon";
import NoDataFound from "@/components/NoDataFound/NoDataFound";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal/ConfirmDeleteModal";
import toast from "@/lib/toast";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import subscriptionService from "@/services/api/subscription";
import type { SubscriptionPlan } from "@/services/api/subscription";
import PlanCard from "./components/PlanCard";
import CreatePlanModal from "./components/CreatePlanModal";
import styles from "./Subscriptions.module.scss";
import { formatRevenue } from "@/lib/utils";

// No endpoint exists yet for org-wide revenue/membership totals — these
// stay hardcoded (same convention as Members.tsx's `MEMBER_STATS`). Plan
// count now comes from the real `GET /subscription-plan` list below.
const TOTAL_ACTIVE_MEMBERSHIPS = 1284;

const Subscriptions = () => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreatePlanModalOpen, setIsCreatePlanModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [planToDelete, setPlanToDelete] = useState<SubscriptionPlan | null>(null);

  const [totalActiveMemberships, setTotalActiveMemberships] = useState(0);
  const [annualRecurringRevenue, setAnnualRecurringRevenue] = useState(0);

  // Plans are only ever needed on this page, so they're fetched directly
  // through subscriptionService rather than routed through Redux (unlike
  // Members.tsx's `GET /users`, which several pages share via userSlice).
  const fetchPlans = useCallback(async () => {
    setIsFetching(true);
    setError(null);
    try {
      const result = await subscriptionService.getSubscriptionPlans({ pageNo: 1, perPage: 60 });
      setPlans(result?.data?.results ?? []);
      setTotalPlans(result?.data?.totalRecord ?? 0);
      setTotalActiveMemberships(result?.data?.totalActiveMemberships ?? 0);
      setAnnualRecurringRevenue(result?.data?.annualRecurringRevenue ?? 0);
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — this message is just for the inline
      // retry state below.
      setPlans([]);
      setError("Failed to load subscription plans");
    } finally {
      setIsFetching(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  usePageSubtitle(
    isFetching
      ? "Loading plans…"
      : error
        ? "Couldn't load plans"
        : `${totalPlans} plans · ${totalActiveMemberships.toLocaleString("en-IN")} active memberships · ${formatRevenue(annualRecurringRevenue)} ARR`,
  );

  const SUBSCRIPTION_STATS: StatCardData[] = [
    {
      label: "Annual Recurring Revenue",
      value: formatRevenue(annualRecurringRevenue),
      // delta: "+6.1%",
      // caption: "vs last quarter",
    },
    {
      label: "Active memberships",
      value: totalActiveMemberships.toLocaleString("en-IN"),
      caption: `across ${totalPlans} plans`,
    },
  ];

  // `isFetching` alone can't tell a first load apart from a background
  // refetch — render nothing (rather than a spinner, or falling through to
  // `NoDataFound` and briefly claiming there are no plans) until there's
  // either real data or an error to show.
  const isInitialLoad = isFetching && plans.length === 0;

  let plansContent = null;
  if (isInitialLoad) {
    // Nothing rendered while the first fetch is in flight — no spinner.
  } else if (error) {
    plansContent = (
      <div className={styles.stateBox}>
        <p className={styles.stateTitle}>Couldn't load plans</p>
        <p className={styles.stateCaption}>{error}</p>
        <Button type="button" onClick={fetchPlans}>
          Try again
        </Button>
      </div>
    );
  } else if (plans.length === 0) {
    plansContent = <NoDataFound itemName="plan" />;
  } else {
    plansContent = (
      <div className={styles.plansGrid}>
        {plans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} onEdit={setEditingPlan} onDelete={setPlanToDelete} />
        ))}
      </div>
    );
  }

  const closeCreatePlanModal = () => {
    setIsCreatePlanModalOpen(false);
    setEditingPlan(null);
  };

  const handleConfirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await subscriptionService.deleteSubscriptionPlan(planToDelete.id);
      toast.success(`${planToDelete.name} has been deleted.`);
      fetchPlans();
    } catch {
      // privateRequest's response interceptor already shows an error toast
      // (see services/axios.ts) — nothing more to do here.
    }
  };

  return (
    <div className={styles.subscriptions}>
      <CreatePlanModal
        isOpen={isCreatePlanModalOpen || editingPlan !== null}
        planToEdit={editingPlan}
        onClose={closeCreatePlanModal}
        onSaved={fetchPlans}
      />
      <ConfirmDeleteModal
        isOpen={planToDelete !== null}
        onClose={() => setPlanToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Plan"
        message={
          <>
            Are you sure you want to delete <strong>{planToDelete?.name || "this plan"}</strong>? This action cannot
            be undone.
          </>
        }
      />

      <StatCardsRow stats={SUBSCRIPTION_STATS} />

      <div className={styles.plansHeader}>
        <h2 className={styles.plansTitle}>Plans</h2>
        {/* <Button className={styles.createPlanButton} onClick={() => setIsCreatePlanModalOpen(true)}>
          <span className={styles.createPlanButtonContent}>
            <PlusIcon width={14} height={14} />
            CREATE PLAN
          </span>
        </Button> */}
      </div>

      {plansContent}
    </div>
  );
};

export default Subscriptions;
